import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import GroupIcon from '@mui/icons-material/Group';
import { getMeCached } from '../authCache';
import { fetchClasses } from '../stargarden/api/starGardenApi';
import {
  fetchLibraryBooks,
  fetchLibraryClassStudents,
  fetchLibraryStudentStatistics,
  markLibraryCompletedBook,
  unmarkLibraryCompletedBook,
  fetchLibraryPointEvents,
} from '../api/library';
import { getGradeLevelFromClassName } from './utils/libraryScoring';
import LibraryPointAnimations from './components/LibraryPointAnimations';

const normalizeStudents = (items) => {
  return (items || [])
    .map((item) => {
      const idRaw = item?.id ?? item?.student_id;
      if (!idRaw) return null;
      const id = String(idRaw);
      const firstName = String(item?.name ?? item?.first_name ?? '').trim();
      const lastName = String(item?.surname ?? item?.last_name ?? '').trim();
      const studentNo = Number(item?.student_no ?? item?.no) || 0;
      return {
        id,
        studentNo,
        firstName,
        lastName,
        label: `${studentNo ? `${studentNo} - ` : ''}${firstName} ${lastName}`.trim() || id,
      };
    })
    .filter(Boolean)
    .sort((a, b) => {
      const aName = `${a.firstName} ${a.lastName}`.trim();
      const bName = `${b.firstName} ${b.lastName}`.trim();
      return aName.localeCompare(bName, 'tr-TR', { sensitivity: 'base' }) || a.id.localeCompare(b.id, 'tr-TR', { numeric: true });
    });
};

const normalizeBooks = (items) => {
  return (items || [])
    .map((item) => {
      const idRaw = item?.id;
      if (!idRaw) return null;
      const title = String(item?.title || '').trim();
      return {
        id: String(idRaw),
        title,
        author: item?.author || '',
        page_count: item?.page_count ?? null,
        pageCount: Number(item?.page_count) || 0,
        type: String(item?.type || ''),
        grade: String(item?.grade || ''),
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.title.localeCompare(b.title, 'tr-TR'));
};

const LibraryBookCompletionPage = () => {
  const navigate = useNavigate();
  const todayTR = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Istanbul',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
  const [tabKey, setTabKey] = useState('bookFirst');
  const [schoolId, setSchoolId] = useState(null);
  const [classes, setClasses] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState('');
  const [students, setStudents] = useState([]);
  const [books, setBooks] = useState([]);
  const [completedMap, setCompletedMap] = useState(new Set());

  const [selectedBookId, setSelectedBookId] = useState('');
  const [selectedStudentId, setSelectedStudentId] = useState('');
  const [bookSearch, setBookSearch] = useState('');
  const [studentSearch, setStudentSearch] = useState('');
  const [finishedDate, setFinishedDate] = useState(todayTR);

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [savingById, setSavingById] = useState({});
  const [savingBookById, setSavingBookById] = useState({});
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setError('');
        const me = await getMeCached();
        const resolvedSchoolId = me?.data?.admin_user?.school_id;
        if (!me?.ok || !resolvedSchoolId) {
          throw new Error('Okul bilgisi bulunamadı.');
        }
        if (!cancelled) setSchoolId(resolvedSchoolId);
      } catch (e) {
        if (!cancelled) {
          setError(e?.message || 'Sayfa yüklenemedi.');
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!schoolId) return;
    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        const list = await fetchClasses(schoolId);
        if (cancelled) return;
        const sorted = (list || []).sort((a, b) =>
          String(a?.name || '').localeCompare(String(b?.name || ''), 'tr-TR'),
        );
        setClasses(sorted);
        if (sorted.length === 1) {
          setSelectedClassId(String(sorted[0].id || ''));
        }
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Sınıflar yüklenemedi.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [schoolId]);

  const selectedClass = useMemo(
    () => classes.find((cls) => String(cls?.id) === String(selectedClassId)) || null,
    [classes, selectedClassId],
  );

  useEffect(() => {
    if (!schoolId || !selectedClass?.id) return;
    let cancelled = false;

    (async () => {
      try {
        setBusy(true);
        setError('');
        setSuccess('');

        const grade = getGradeLevelFromClassName(selectedClass?.name);
        const [studentRows, bookRows, stats] = await Promise.all([
          fetchLibraryClassStudents(schoolId, selectedClass.id),
          fetchLibraryBooks(schoolId, grade ? { grade } : {}),
          fetchLibraryStudentStatistics(schoolId, { className: selectedClass?.name }),
        ]);

        if (cancelled) return;

        const normalizedStudents = normalizeStudents(studentRows || []);
        const normalizedBooks = normalizeBooks(bookRows || []);

        const map = new Set();
        (stats?.students || []).forEach((student) => {
          const sid = String(student?.id || '');
          (student?.completedBooks || []).forEach((book) => {
            const bid = String(book?.bookId || '');
            if (sid && bid) {
              map.add(`${sid}:${bid}`);
            }
          });
        });

        setStudents(normalizedStudents);
        setBooks(normalizedBooks);
        setCompletedMap(map);
        setSelectedStudentId('');
        setSavingBookById({});
        setSelectedBookId((prev) => {
          if (prev && normalizedBooks.some((book) => book.id === prev)) return prev;
          return normalizedBooks[0]?.id || '';
        });
      } catch (e) {
        if (!cancelled) {
          setStudents([]);
          setBooks([]);
          setCompletedMap(new Set());
          setError(e?.message || 'Öğrenci/kitap verileri yüklenemedi.');
        }
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [schoolId, selectedClass?.id, selectedClass?.name]);

  const filteredBooks = useMemo(() => {
    const query = bookSearch.trim().toLocaleLowerCase('tr-TR');
    if (!query) return books;
    return books.filter((book) => book.title.toLocaleLowerCase('tr-TR').includes(query));
  }, [bookSearch, books]);

  const filteredStudents = useMemo(() => {
    const query = studentSearch.trim().toLocaleLowerCase('tr-TR');
    if (!query) return students;
    return students.filter((student) => student.label.toLocaleLowerCase('tr-TR').includes(query));
  }, [studentSearch, students]);

  const selectedBook = useMemo(
    () => books.find((book) => book.id === selectedBookId) || null,
    [books, selectedBookId],
  );

  const maxNoLen = useMemo(() => {
    return (students || []).reduce((max, student) => {
      const no = String(student?.studentNo || student?.id || '');
      return Math.max(max, no.length);
    }, 0);
  }, [students]);

  const selectedStudent = useMemo(
    () => students.find((student) => String(student.id) === String(selectedStudentId)) || null,
    [students, selectedStudentId],
  );

  const handleMarkSingle = async (studentId, bookId, isDone) => {
    if (!schoolId || !studentId || !bookId) return;
    const sid = String(studentId);
    try {
      setSavingById((prev) => ({ ...prev, [sid]: true }));
      setError('');
      setSuccess('');
      if (isDone) {
        await unmarkLibraryCompletedBook(schoolId, { studentId, bookId });
        setCompletedMap((prev) => {
          const next = new Set(prev);
          next.delete(`${String(studentId)}:${String(bookId)}`);
          return next;
        });
        setSuccess('İşaret kaldırıldı.');
      } else {
        const response = await markLibraryCompletedBook(schoolId, {
          studentId,
          bookId,
          finishedDate: finishedDate || undefined,
        });

        setCompletedMap((prev) => {
          const next = new Set(prev);
          next.add(`${String(studentId)}:${String(bookId)}`);
          return next;
        });

        setSuccess(
          response?.created
            ? 'Kitap okundu olarak işaretlendi.'
            : 'Kayıt güncellendi (zaten vardı).',
        );
      }
    } catch (e) {
      setError(e?.message || 'İşlem başarısız oldu.');
    } finally {
      setSavingById((prev) => ({ ...prev, [sid]: false }));
    }
  };

  const handleMarkSelectedStudentBook = async (bookId) => {
    if (!schoolId || !bookId || !selectedStudentId) return;
    const sid = String(selectedStudentId);
    const bid = String(bookId);
    const key = `${sid}:${bid}`;
    const isDone = completedMap.has(key);
    try {
      setSavingBookById((prev) => ({ ...prev, [bid]: true }));
      setError('');
      setSuccess('');
      if (isDone) {
        await unmarkLibraryCompletedBook(schoolId, {
          studentId: sid,
          bookId,
        });
        setCompletedMap((prev) => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
        setSuccess('İşaret kaldırıldı.');
      } else {
        const response = await markLibraryCompletedBook(schoolId, {
          studentId: sid,
          bookId,
          finishedDate: finishedDate || undefined,
        });

        setCompletedMap((prev) => {
          const next = new Set(prev);
          next.add(key);
          return next;
        });

        setSuccess(response?.created ? 'Kitap okundu olarak işaretlendi.' : 'Kayıt güncellendi (zaten vardı).');
      }
    } catch (e) {
      setError(e?.message || 'İşlem başarısız oldu.');
    } finally {
      setSavingBookById((prev) => ({ ...prev, [bid]: false }));
    }
  };

  const canRenderContent = !loading && schoolId && selectedClass;
  const loadPointEvents = useCallback((after) => fetchLibraryPointEvents(schoolId, selectedClass?.id, after), [schoolId, selectedClass?.id]);
  const refreshCompletedBooks = async () => {
    const stats = await fetchLibraryStudentStatistics(schoolId, { className: selectedClass.name });
    setCompletedMap(new Set((stats.students || []).flatMap((student) => (student.completedBooks || []).map((book) => `${student.id}:${book.bookId}`))));
  };

  const classOptions = useMemo(() => classes || [], [classes]);

  return (
    <Box
      sx={{
        minHeight: '100dvh',
        width: '100%',
        p: { xs: 1.5, md: 2 },
      }}
    >
      <Stack spacing={1.5}>
        <Paper
          sx={{ p: { xs: 1.4, sm: 1.8 }, borderRadius: 2 }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.2 }}>
            <Tooltip title="Panele dön">
              <IconButton onClick={() => navigate('/admin')} size="small">
                <ArrowBackIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.1rem', sm: '1.3rem' } }}>
              Kitap Okundu İşaretle
            </Typography>
          </Box>
          <Grid container spacing={1.5} alignItems="center">
            <Grid item xs={12} md={6}>
              <Autocomplete
                size="small"
                options={classOptions}
                value={selectedClass || null}
                loading={loading}
                getOptionLabel={(option) => String(option?.name || '')}
                isOptionEqualToValue={(option, value) => String(option?.id) === String(value?.id)}
                onChange={(_, value) => setSelectedClassId(String(value?.id || ''))}
                renderInput={(params) => (
                  <TextField {...params} label="Şube" placeholder="Seçin" fullWidth />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                size="small"
                type="date"
                label="Tamamlanma Tarihi"
                InputLabelProps={{ shrink: true }}
                value={finishedDate}
                onChange={(e) => setFinishedDate(String(e.target.value || ''))}
                fullWidth
              />
            </Grid>
          </Grid>
        </Paper>

        {error ? <Alert severity="error">{error}</Alert> : null}
        {success ? <Alert severity="success">{success}</Alert> : null}

        {(loading || busy) && (
          <Paper sx={{ borderRadius: 2, p: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={20} />
            <Typography color="text.secondary">İşleniyor...</Typography>
          </Paper>
        )}

        {!loading && !selectedClass ? (
          <Alert severity="info">İşlem için bir sınıf seçin.</Alert>
        ) : null}

        {canRenderContent ? (
          <>
          <LibraryPointAnimations loadEvents={loadPointEvents} onAwards={refreshCompletedBooks} />
          <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
            <Tabs
              value={tabKey}
              onChange={(_, val) => setTabKey(val)}
              variant="fullWidth"
              sx={{ borderBottom: '1px solid', borderColor: 'divider' }}
            >
              <Tab
                value="bookFirst"
                icon={<MenuBookIcon fontSize="small" />}
                iconPosition="start"
                label="Önce Kitap Seç"
                sx={{ textTransform: 'none', fontWeight: 700 }}
              />
              <Tab
                value="studentFirst"
                icon={<GroupIcon fontSize="small" />}
                iconPosition="start"
                label="Önce Öğrenci Seç"
                sx={{ textTransform: 'none', fontWeight: 700 }}
              />
            </Tabs>

            <Box sx={{ p: { xs: 1.2, md: 1.5 } }}>
              {tabKey === 'bookFirst' ? (
                <>
                  <Paper sx={{ p: { xs: 1.2, sm: 1.5 }, mb: 1.2, borderRadius: 2 }}>
                    <Grid container spacing={1.2} alignItems="center">
                      <Grid item xs={12} md={6}>
                        <FormControl size="small" fullWidth>
                          <InputLabel id="library-completion-book">Kitap</InputLabel>
                          <Select
                            labelId="library-completion-book"
                            value={selectedBookId}
                            label="Kitap"
                            onChange={(e) => setSelectedBookId(String(e.target.value || ''))}
                          >
                            {filteredBooks.map((book) => (
                              <MenuItem key={book.id} value={book.id}>
                                {book.title}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <OutlinedInput
                          value={studentSearch}
                          onChange={(e) => setStudentSearch(e.target.value)}
                          size="small"
                          fullWidth
                          placeholder="Öğrenci ara"
                          startAdornment={(
                            <InputAdornment position="start">
                              <SearchIcon fontSize="small" />
                            </InputAdornment>
                          )}
                        />
                      </Grid>
                    </Grid>
                  </Paper>

                  <Paper sx={{ p: { xs: 1.2, sm: 1.5 }, borderRadius: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.15rem' }, mb: 1 }}>
                      Öğrenci Durumları
                    </Typography>
                    {busy ? (
                      <List>
                        {Array.from({ length: 6 }).map((_, i) => (
                          <ListItem key={`sk-book-${i}`} divider>
                            <Skeleton variant="text" width="60%" />
                          </ListItem>
                        ))}
                      </List>
                    ) : filteredStudents.length === 0 ? (
                      <Box sx={{ py: 2, textAlign: 'center', color: 'text.secondary' }}>
                        <Typography>Öğrenci bulunamadı.</Typography>
                      </Box>
                    ) : (
                      <List disablePadding>
                        {filteredStudents.map((student) => {
                          const isDone = selectedBook ? completedMap.has(`${student.id}:${selectedBook.id}`) : false;
                          const rowSaving = Boolean(savingById[String(student.id)]);
                          const no = String(student.studentNo || student.id || '');
                          const widthCh = Math.max(maxNoLen || 0, no.length);
                          return (
                            <ListItem key={student.id} divider disableGutters sx={{ px: { xs: 0.5, sm: 0.5 }, py: 0.85 }}>
                              <Box
                                sx={{
                                  width: '100%',
                                  display: 'grid',
                                  alignItems: 'center',
                                  gap: 1,
                                  gridTemplateColumns: { xs: '1fr 138px', sm: '1fr 150px' },
                                }}
                              >
                                <Typography
                                  variant="body1"
                                  sx={{
                                    fontWeight: 600,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 1, whiteSpace: 'nowrap' }}>
                                    <Box
                                      component="span"
                                      sx={{
                                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                                        display: 'inline-block',
                                        width: `${widthCh}ch`,
                                        whiteSpace: 'pre',
                                      }}
                                    >
                                      {no.padEnd(widthCh, ' ')}
                                    </Box>
                                    <Box component="span">{student.firstName} {student.lastName}</Box>
                                  </Box>
                                </Typography>

                                <ButtonGroup
                                  size="small"
                                  sx={{
                                    justifySelf: 'end',
                                    borderRadius: '999px',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    '& .MuiButtonGroup-grouped': {
                                      textTransform: 'none',
                                      fontWeight: 700,
                                      minWidth: { xs: 110, sm: 120 },
                                    },
                                  }}
                                >
                                  <Button
                                    color={isDone ? 'success' : 'primary'}
                                    variant={isDone ? 'outlined' : 'contained'}
                                    startIcon={<DoneAllIcon fontSize="small" />}
                                    onClick={() => handleMarkSingle(student.id, selectedBook?.id, isDone)}
                                    disabled={!selectedBook || rowSaving}
                                  >
                                    {rowSaving ? 'Kaydediliyor...' : isDone ? 'İşaretli' : 'Okudu'}
                                  </Button>
                                </ButtonGroup>
                              </Box>
                            </ListItem>
                          );
                        })}
                      </List>
                    )}
                  </Paper>
                </>
              ) : (
                <Paper sx={{ p: { xs: 1.2, sm: 1.5 }, borderRadius: 2 }}>
                  <Grid container spacing={1.2} alignItems="center" sx={{ mb: 1 }}>
                    <Grid item xs={12} md={6}>
                      <Autocomplete
                        size="small"
                        options={students}
                        value={selectedStudent}
                        getOptionLabel={(option) => String(option?.label || '')}
                        isOptionEqualToValue={(option, value) => String(option?.id) === String(value?.id)}
                        onChange={(_, value) => setSelectedStudentId(String(value?.id || ''))}
                        renderInput={(params) => (
                          <TextField {...params} label="Öğrenci Seçimi" placeholder="Öğrenci seçin" fullWidth />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <OutlinedInput
                        value={bookSearch}
                        onChange={(e) => setBookSearch(e.target.value)}
                        size="small"
                        fullWidth
                        placeholder="Kitap ara"
                        startAdornment={(
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        )}
                      />
                    </Grid>
                  </Grid>

                  <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.15rem' }, mb: 1 }}>
                    Kitaplar
                  </Typography>
                  {!selectedStudent ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      Lütfen önce bir öğrenci seçin.
                    </Typography>
                  ) : null}

                  <List disablePadding sx={{ maxHeight: 450, overflowY: 'auto' }}>
                    {filteredBooks.map((book) => {
                      const isDone = selectedStudent
                        ? completedMap.has(`${selectedStudent.id}:${book.id}`)
                        : false;
                      const bookSaving = Boolean(savingBookById[String(book.id)]);
                      return (
                        <ListItem key={book.id} divider disableGutters sx={{ px: { xs: 0.5, sm: 0.5 }, py: 0.85 }}>
                          <Box
                            sx={{
                              width: '100%',
                              display: 'grid',
                              alignItems: 'center',
                              gap: 1,
                              gridTemplateColumns: { xs: '1fr 138px', sm: '1fr 180px' },
                            }}
                          >
                            <Box sx={{ minWidth: 0 }}>
                              <Typography sx={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {book.title}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {book.pageCount || 0} sayfa
                              </Typography>
                            </Box>
                            <Button
                              size="small"
                              variant={isDone ? 'outlined' : 'contained'}
                              color={isDone ? 'success' : 'primary'}
                              startIcon={<DoneAllIcon fontSize="small" />}
                              onClick={() => handleMarkSelectedStudentBook(book.id)}
                              disabled={!selectedStudent || bookSaving}
                              sx={{ textTransform: 'none', fontWeight: 700, justifySelf: 'end', minWidth: { xs: 110, sm: 120 } }}
                            >
                              {bookSaving ? 'Kaydediliyor...' : isDone ? 'İşaretli' : 'Okudu'}
                            </Button>
                          </Box>
                        </ListItem>
                      );
                    })}
                  </List>
                </Paper>
              )}
            </Box>
          </Paper>
          </>
        ) : null}
      </Stack>
    </Box>
  );
};

export default LibraryBookCompletionPage;
